// Frozen gameplay + pipeline metrics for the Nihil Project.
//
// This module is the SINGLE SOURCE OF TRUTH for level dimensions, the
// Blender unit scale, and per-variant performance budgets. Engine code
// imports from here; the Blender pipeline reference doc
// (`docs/level-metrics.md`) mirrors these numbers verbatim.
//
// Anything that changes here is a pipeline-wide change — bump the
// METRICS_VERSION and re-export every level.

import { CELL_SIZE, ROOM_COLS, ROOM_ROWS } from './rooms/templates.js';

export const METRICS_VERSION = '1.0.0';

// 1 unit = 1 meter. Blender scene unit must be set to "Meters" with a
// scale of 1.0 before exporting glTF/FBX. Three.js consumes meters
// directly, so no axis or scale conversion happens at import.
export const UNIT_SCALE = Object.freeze({
  system: 'metric',
  unit: 'meter',
  blenderUnitsPerMeter: 1,
  engineUnitsPerMeter: 1,
  forwardAxis: '-Z',
  upAxis: '+Y',
});

// Locked level grid. The voxel kitbash snaps to GRID_SIZE; corridors,
// doors, and cover are all multiples of this value so modular pieces
// remain interchangeable across variants.
export const GRID = Object.freeze({
  cellSize: CELL_SIZE,           // 2 m per grid cell
  snapIncrement: CELL_SIZE / 4,  // 0.5 m sub-grid for kitbash detail
  roomCols: ROOM_COLS,           // 20 cells wide
  roomRows: ROOM_ROWS,           // 20 cells deep
  roomWidth: CELL_SIZE * ROOM_COLS,  // 40 m
  roomDepth: CELL_SIZE * ROOM_ROWS,  // 40 m
});

// Player envelope used by collision, camera framing, and any hand-modeled
// mantling/cover geometry. Top-down game, so jump height is intentionally
// zero — step height stays low to keep the navmesh-style XZ resolver
// trivial.
export const PLAYER = Object.freeze({
  radius: 0.55,        // m, capsule radius in XZ
  height: 1.8,         // m, eye-to-floor reference for prop scale
  baseSpeed: 7.0,      // m/s
  dashSpeed: 22,       // m/s
  maxStepHeight: 0.0,  // top-down: no vertical traversal
  maxJumpHeight: 0.0,
});

// Architectural minimums. All level pieces — base and variants — must
// honor these so the player and enemies traverse predictably.
export const ARCHITECTURE = Object.freeze({
  doorWidth: CELL_SIZE * 1.05, // 2.1 m, matches Room._buildDoor()
  doorHeight: 2.2,             // m
  corridorWidth: CELL_SIZE,    // 2 m, exactly one cell
  wallHeight: 2.5,             // m, matches Room build
  wallThickness: CELL_SIZE,    // 2 m for full-cell walls
  coverHeightLow: 0.9,         // m, blocks line-of-sight while crouched-equivalent
  coverHeightHigh: 1.6,        // m, full standing cover
  ceilingClearance: 3.0,       // m, reserved headroom above wallHeight
});

// Three variant identities. Layout work and kitbash density should pull
// directly from these knobs so playtests stay comparable.
export const VARIANTS = Object.freeze({
  A: Object.freeze({
    role: 'balanced',
    avgEngagementRangeMeters: 12,
    coverDensityPerCell: 0.18,
    sightlineMaxMeters: 28,
  }),
  B: Object.freeze({
    role: 'tight-close-combat',
    avgEngagementRangeMeters: 6,
    coverDensityPerCell: 0.32,
    sightlineMaxMeters: 14,
  }),
  C: Object.freeze({
    role: 'open-vertical',
    avgEngagementRangeMeters: 20,
    coverDensityPerCell: 0.10,
    sightlineMaxMeters: 40,
  }),
});

// Per-variant budgets. These are ceilings, not targets — the perf gate
// in Section 9 of the plan must measure against them on the lowest
// supported hardware (integrated-GPU laptop @ 1080p / 60fps).
export const BUDGETS = Object.freeze({
  triangles: 120_000,       // per level, render mesh only (excludes player+enemies)
  drawCalls: 180,           // per frame, steady-state mid-room
  uniqueMaterials: 8,       // shared aggressively across kitbash
  textureMemoryMB: 48,      // GPU-resident textures for the level
  collisionTriangles: 8_000, // simplified collision proxy budget
  pointLights: 6,           // per room, matches Room accent palette length
});

export const METRICS = Object.freeze({
  version: METRICS_VERSION,
  unitScale: UNIT_SCALE,
  grid: GRID,
  player: PLAYER,
  architecture: ARCHITECTURE,
  variants: VARIANTS,
  budgets: BUDGETS,
});

export default METRICS;
